import { useEffect, useRef, useState, type CSSProperties } from "react";
import { IconSearch, IconCheck } from "./icons";
import { PeriodoFiltro } from "./PeriodoFiltro";
import { BADGE_TONI, type StatoTone } from "./statoToni";

/** Filtro fornitore = SOLO visualizzazione. Multi-selezione (richiesta
 *  Davide 2026-05-18: "elenco a discesa, selezionare quelli che voglio
 *  vedere, anche piu' assieme"). Set vuoto = nessun filtro (tutti).
 *  Le opzioni arrivano da OrdiniPage: anagrafica fornitori + chiunque il
 *  CATALOGO attribuisca agli ordini caricati (``fornitoreFiltro.ts``). */

/** Opzioni da mostrare = quelle correnti + le selezioni che non ci sono piu'.
 *  Le opzioni sono dinamiche (fornitori e canali degli ordini caricati): se
 *  Davide sceglie un fornitore e poi stringe le date, quella voce sparisce ma
 *  la selezione resta in sessionStorage. Senza questa riga il filtro restava
 *  attivo con l'etichetta VUOTA: lista a zero ordini e niente da togliere.
 *  Cosi' invece resta visibile e si smarca. */
export function withSelected(
  options: { value: string; label: string }[],
  selected: ReadonlySet<string>,
): { value: string; label: string }[] {
  const orfane = [...selected]
    .filter((v) => !options.some((o) => o.value === v))
    .map((v) => ({ value: v, label: v }));
  return orfane.length === 0 ? options : [...options, ...orfane];
}

function filterSummary(
  options: { value: string; label: string }[],
  selected: ReadonlySet<string>,
  allText: string,
): string {
  if (selected.size === 0) return allText;
  return options
    .filter((o) => selected.has(o.value))
    .map((o) => o.label)
    .join(", ");
}

// Il colore di un chip e' quello del BADGE DI STATO che pesca (Davide
// 2026-09-23: «tutti i colori nello stile delle etichette di stato»): il filtro
// «Pagato» e' verde come il badge PAGATO, «Spediti» blu come SPEDITO. Prima
// erano tinte pastello di una palette diversa, e filtro e badge si leggevano
// come due cose.
export interface ChipDef {
  id: string;
  label: string;
  tono: StatoTone;
}

export const CHIPS: ChipDef[] = [
  // Gli ordini da evadere portano il badge IN CORSO.
  { id: "da_evadere", label: "Da evadere", tono: "in-corso" },
  // Il giallo del badge BONIFICO: il pagamento che si aspetta.
  { id: "attesa_pagamento", label: "Attesa pagamento", tono: "bonifico" },
  { id: "spediti", label: "Spediti", tono: "spedito" },
  { id: "annullati", label: "Annullati", tono: "annullato" },
  { id: "pronta_spedizione", label: "Spedizione Pronta", tono: "pronto-ritiro" },
  // Rimborsi (item 20): ordini con un reso/sostituzione in corso o chiuso
  // (predicato hasReturn in OrdiniPage). L'indaco dei badge «Sost.».
  { id: "rimborsi", label: "Rimborsi", tono: "sost-fornitore" },
];

// Riga PAGAMENTO/INVIO consolidata (F1 2026-07-14). 5 chip cross-fornitore
// (prima erano 7 granulari legati ai singoli PaymentState AS/2gg). "Non
// ordinato"/"Inviato" ora coprono QUALSIASI fornitore con un flusso d'invio
// (supplierSendState), "Da pagare" include anche gli spediti col fornitore
// ancora da pagare (supplierUnpaid, item 13). La logica di matching (OR
// paymentState + flag) vive in OrdiniPage (STATE_CHIP_TO_PAYMENT +
// STATE_CHIP_OR_PREDICATES).
// Le etichette sono le STESSE dei badge in card (Davide 2026-09-01): un filtro
// che si chiama diverso dal badge che deve pescare costringe a tradurre a mente.
export const PAYMENT_CHIPS: ChipDef[] = [
  { id: "s_not_ordered", label: "Non ordinato", tono: "non-ordinato" },
  { id: "s_sent", label: "Ordinato", tono: "ordinato" },
  { id: "s_unpaid", label: "Da pagare", tono: "da-pagare" },
  { id: "s_paid", label: "Pagato", tono: "pagato" },
  { id: "s_awaiting_payment", label: "Da incassare", tono: "da-incassare" },
];

// Id delle due righe di chip, esportati come FONTE UNICA per il filtraggio in
// OrdiniPage: OR dentro la riga, AND fra le righe (item 2). Tenerli derivati da
// CHIPS/PAYMENT_CHIPS evita che aggiungere un chip qui lo lasci fuori dal filtro.
export const MACRO_CHIP_IDS: string[] = CHIPS.map((c) => c.id);
export const PAYMENT_CHIP_IDS: string[] = PAYMENT_CHIPS.map((c) => c.id);

// Acceso = il badge stesso (pieno, testo bianco) e PREMUTO (ombra interna);
// spento = il suo contorno. La spunta a sinistra non c'e' piu' (Davide
// 2026-09-23: «togli lo spazio vuoto a sinistra della scritta»). Il tono
// arriva in `--tono` e le classi sono fisse: il JIT di Tailwind non vede
// colori composti a runtime.
// Redesign 2026-10-27: il chip spento e' bianco con testo nero e il pallino
// del suo colore; acceso e' il badge pieno, come prima. Il colore resta quello
// del badge di stato (un colore solo in tutta la pagina), ma il testo spento
// non e' piu' colorato: nero si legge meglio (PRODUCT.md, testo sempre nero).
const CHIP_ACCESO =
  "border-[color:var(--tono)] bg-[color:var(--tono)] text-white shadow-[inset_0_1px_2px_rgba(31,27,22,0.3)]";
const CHIP_SPENTO =
  "border-line-2 bg-card text-ink hover:border-[color:var(--tono)] hover:bg-[color:color-mix(in_srgb,var(--tono)_6%,rgb(var(--om-card)))]";

interface OrdiniFilterCardProps {
  search: string;
  onSearchChange: (v: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  activeChips: Set<string>;
  onChipToggle: (id: string) => void;
  onClearSearch: () => void;
  onShowAll: () => void;
  /** Torna ai filtri di partenza: «Da evadere» negli ultimi 30 giorni. */
  onReset: () => void;
  /** Opzioni fornitore DINAMICHE (anagrafica + catalogo degli ordini). */
  supplierOptions: { value: string; label: string }[];
  /** Id fornitore selezionati (multi). Set vuoto = nessun filtro. */
  selectedSuppliers: ReadonlySet<string>;
  /** Riceve sempre un NUOVO Set (immutabile). */
  onSupplierFilterChange: (next: Set<string>) => void;
  /** Opzioni canale di vendita DINAMICHE dagli ordini caricati (label=value). */
  channelOptions: { value: string; label: string }[];
  /** Canali selezionati (multi). Set vuoto = tutti i canali. */
  selectedChannels: ReadonlySet<string>;
  onChannelFilterChange: (next: Set<string>) => void;
  /** Quanti ordini entrano in ogni chip (id → numero). Assente = niente numeri. */
  conteggi?: Readonly<Record<string, number>>;
}

/** Dropdown multi-select generico (solo visualizzazione), riusato per il filtro
 *  Fornitore e per il filtro Canale di vendita (item 5). Parametrizzato su
 *  label/allText/options: la UI e' identica, cambia solo la sorgente delle
 *  opzioni. Set vuoto = nessun filtro (tutti). */
function FilterMultiSelect({
  label,
  allText,
  options,
  selected,
  onChange,
}: {
  label: string;
  allText: string;
  options: { value: string; label: string }[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const voci = withSelected(options, selected);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function toggle(value: string) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  }

  // Scelta attiva = contorno scuro anche a tendina CHIUSA: un filtro che non si
  // vede e' una lista corta senza un perche' (2026-09-23, plancia unica). Da
  // spento il riepilogo «Tutti i fornitori» dice gia' cosa filtra: l'etichetta
  // compare solo quando serve a leggere il valore scelto.
  const attivo = selected.size > 0;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={`Filtra per ${label.toLowerCase()}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-attivo={attivo || undefined}
        onClick={() => setOpen((v) => !v)}
        className={[
          "flex h-[34px] items-center gap-[6px] rounded-full border px-3 text-[12.5px] transition-colors",
          attivo ? "border-ink bg-card" : "border-line-2 bg-card hover:border-ink-4",
        ].join(" ")}
      >
        {attivo && <span className="text-ink-3">{label}</span>}
        <span
          className={`${attivo ? "font-semibold" : "font-medium"} text-ink max-w-[220px] truncate`}
        >
          {filterSummary(voci, selected, allText)}
        </span>
        <svg
          className="w-3 h-3 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round] text-ink-3"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute top-[calc(100%+6px)] left-0 min-w-[200px] bg-card border border-line-2 rounded-lg shadow-popover py-1.5 z-[60]"
        >
          <button
            type="button"
            role="option"
            aria-selected={selected.size === 0}
            onClick={() => onChange(new Set())}
            className="flex items-center gap-2 w-full px-[12px] py-[7px] text-left text-[12.5px] hover:bg-paper-2 transition-colors"
          >
            <span className="text-ink-2">{allText}</span>
            {selected.size === 0 && (
              <IconCheck className="w-[11px] h-[11px] ml-auto fill-none stroke-accent stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]" />
            )}
          </button>
          <div className="my-1 border-t border-line" />
          {voci.map((opt) => {
            const on = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(opt.value)}
                className="flex items-center gap-2 w-full px-[12px] py-[7px] text-left text-[12.5px] hover:bg-paper-2 transition-colors"
              >
                <span
                  className={[
                    "w-[14px] h-[14px] rounded-[3px] border inline-grid place-items-center shrink-0",
                    on ? "bg-accent border-accent" : "bg-card border-line-2",
                  ].join(" ")}
                  aria-hidden="true"
                >
                  <IconCheck
                    className={[
                      "w-[10px] h-[10px] fill-none stroke-white stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]",
                      on ? "opacity-100" : "opacity-0",
                    ].join(" ")}
                  />
                </span>
                <span className={on ? "font-medium text-ink" : "text-ink-2"}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function OrdiniFilterCard({
  search,
  onSearchChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  activeChips,
  onChipToggle,
  onClearSearch,
  onShowAll,
  onReset,
  supplierOptions,
  selectedSuppliers,
  onSupplierFilterChange,
  channelOptions,
  selectedChannels,
  onChannelFilterChange,
  conteggi,
}: OrdiniFilterCardProps) {
  // Nessuna cornice propria: la barra vive nella plancia unica di OrdiniPage
  // (Davide 2026-09-23), insieme ai tasti azione e ai ritiri.
  return (
    <div className="flex flex-col gap-1.5">
      {/* Riga 1 — ricerca e «Pulisci» a sinistra; periodo, tendine,
          «Reimposta» e «Mostra tutti» a destra. Va a capo sugli schermi
          stretti (flex-wrap). */}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="flex h-[34px] min-w-[200px] max-w-[440px] flex-1 items-center gap-2 rounded-full border border-line-2 bg-card px-3.5 text-[13px] text-ink shadow-[0_1px_2px_rgba(31,27,22,0.04)] transition-[border-color,box-shadow] focus-within:border-ink-3 focus-within:shadow-[0_0_0_3px_rgba(31,27,22,0.06)]"
          aria-label="Cerca ordini"
        >
          <IconSearch className="w-[14px] h-[14px] text-ink-3 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round] shrink-0" />
          <input
            className="border-none bg-transparent flex-1 min-w-0 outline-none placeholder:text-ink-3 text-[13px]"
            placeholder="Cerca ordine, cliente, telefono, indirizzo, tracking, SKU…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Cerca ordini"
          />
        </label>
        {/* «Pulisci» sempre al suo posto (Davide 2026-09-23: «e' stato perso il
            tasto pulisci»): la X che compariva solo col testo non si vedeva. */}
        <button type="button" className={TASTO_TESTO} onClick={onClearSearch}>
          Pulisci
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <PeriodoFiltro
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={onDateFromChange}
            onDateToChange={onDateToChange}
          />

          {/* Filtri multi-select (solo visualizzazione): fornitore + canale di
              vendita. Selezione multipla = OR; nessuna spunta = tutti. Le
              opzioni canale sono dinamiche dagli ordini caricati (OrdiniPage). */}
          <FilterMultiSelect
            label="Fornitore"
            allText="Tutti i fornitori"
            options={supplierOptions}
            selected={selectedSuppliers}
            onChange={onSupplierFilterChange}
          />
          <FilterMultiSelect
            label="Canale"
            allText="Tutti i canali"
            options={channelOptions}
            selected={selectedChannels}
            onChange={onChannelFilterChange}
          />
          {/* Due uscite diverse: «Reimposta» torna al punto di partenza
              (Da evadere, ultimi 30 giorni), «Mostra tutti» spegne ogni filtro. */}
          <button
            type="button"
            className={TASTO_REIMPOSTA}
            title="Torna a «Da evadere» negli ultimi 30 giorni"
            onClick={onReset}
          >
            Reimposta
          </button>
          <button type="button" className={TASTO_TESTO} onClick={onShowAll}>
            Mostra tutti
          </button>
        </div>
      </div>

      {/* Riga 2 — i chip STATO e quelli PAGAMENTO/INVIO sulla stessa riga
          (Davide 2026-09-23), divisi da un filo: dentro un gruppo i chip
          valgono in OR, fra i due gruppi in AND. Il secondo gruppo va a capo
          intero, col suo filo davanti, quando la riga non basta. */}
      <div data-testid="filtri-chip" className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <div className="flex flex-wrap items-center gap-1">
          {CHIPS.map((chip) =>
            renderChip(chip, activeChips.has(chip.id), onChipToggle, conteggi?.[chip.id]),
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span role="separator" aria-orientation="vertical" className="mr-1 h-4 w-px bg-line-2" />
          {PAYMENT_CHIPS.map((chip) =>
            renderChip(chip, activeChips.has(chip.id), onChipToggle, conteggi?.[chip.id]),
          )}
        </div>
      </div>
    </div>
  );
}

/** «Reimposta» porta a «Da evadere»: pieno, del colore di quel chip. */
const TASTO_REIMPOSTA =
  "h-[34px] shrink-0 rounded-full border border-[#c05600] bg-[#c05600] px-3.5 text-[12.5px] font-semibold text-white transition-colors hover:border-[#a24800] hover:bg-[#a24800] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c05600]/40";

/** I tasti di testo della barra (Pulisci, Mostra tutti). */
const TASTO_TESTO =
  "h-[34px] shrink-0 rounded-full px-2 text-[12.5px] font-semibold text-ink underline-offset-4 transition-colors hover:bg-paper-2 hover:underline";

function renderChip(chip: ChipDef, isOn: boolean, onToggle: (id: string) => void, quanti?: number) {
  return (
    <button
      key={chip.id}
      type="button"
      aria-pressed={isOn}
      // Il nome resta l'etichetta: il numero cambia a ogni giro del sync, e un
      // tasto che cambia nome a ogni giro non si ritrova (ne' a voce ne' nei test).
      aria-label={chip.label}
      title={quanti === undefined ? undefined : `${chip.label}: ${quanti} ordini`}
      onClick={() => onToggle(chip.id)}
      style={{ "--tono": BADGE_TONI[chip.tono] } as CSSProperties}
      className={[
        // Redesign 2026-10-27: etichetta normale a 12,5px (era maiuscoletto da
        // 9,5px) e il numero degli ordini accanto. Il maiuscolo piccolo si
        // legge a fatica; i chip vanno a capo su due righe a 1366, e va bene.
        "group inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border pl-2.5 pr-3 text-[12.5px] font-semibold transition-[background-color,color,border-color,box-shadow,transform] duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-1",
        isOn ? CHIP_ACCESO : CHIP_SPENTO,
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "h-2 w-2 shrink-0 rounded-full",
          isOn ? "bg-card" : "bg-[color:var(--tono)]",
        ].join(" ")}
      />
      {chip.label}
      {quanti !== undefined && (
        <span
          data-testid={`chip-conteggio-${chip.id}`}
          className={[
            "min-w-[1.25rem] rounded-full px-1.5 text-center text-[11.5px] font-bold tabular-nums",
            isOn ? "bg-white/20" : "bg-paper-2",
          ].join(" ")}
        >
          {quanti}
        </span>
      )}
    </button>
  );
}
